import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { breakingFeed } from '@/mocks/ranking';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/categories';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';

const DOMAIN = 'https://97go.cn';

const levelMap: Record<string, { label: string; className: string }> = {
  urgent: { label: '紧急', className: 'bg-primary-500 text-background-50' },
  focus: { label: '重点关注', className: 'bg-accent-500 text-background-50' },
  follow: { label: '跟进中', className: 'bg-secondary-200 text-secondary-900' },
};

export default function BreakingPage() {
  useSEO({
    title: '突发新闻 - 正在发生的事件实时播报 | 爆料前线',
    description:
      '爆料前线突发新闻频道按时间倒序播报正在发生的事件，标注紧急程度与最新进展，配合话题时间线，让你第一时间掌握突发动态与后续走向。',
    keywords: '突发新闻,实时播报,事件进展,今日猛料,热点追踪',
    path: '/breaking',
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '突发新闻',
        url: `${DOMAIN}/breaking`,
        inLanguage: 'zh-CN',
        description: '按时间倒序播报正在发生的事件，标注紧急程度与最新进展。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${DOMAIN}/` },
          { '@type': 'ListItem', position: 2, name: '突发新闻', item: `${DOMAIN}/breaking` },
        ],
      },
    ],
  });

  const feedItems = breakingFeed
    .map((item) => {
      const topic = topics.find((t) => t.slug === item.slug);
      return topic ? { ...item, topic } : null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '突发新闻' }]} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-800">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-500 animate-softPulse" />
            正在播报 · 最新更新 {breakingFeed[0].time}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">突发新闻速递</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            按时间倒序排列正在推进的事件，每条都标注了紧急程度。进入话题详情页可以看到完整的节点记录，后续进展会持续追加在同一条话题里。
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <ol className="flex flex-col gap-5">
                {feedItems.map((item) => {
                  const meta = levelMap[item.level] ?? levelMap.follow;
                  return (
                    <li key={item.id} className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-sm font-bold text-foreground-950">{item.time}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] ${meta.className}`}>{meta.label}</span>
                        <span className="text-[11px] text-foreground-500">
                          {categories.find((c) => c.slug === item.topic.categorySlug)?.name}
                        </span>
                      </div>
                      <TopicCard topic={item.topic} layout="horizontal" />
                    </li>
                  );
                })}
              </ol>
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-lg font-bold text-foreground-950">正在冲榜的话题</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  想一次性看清热度排序，可以切换到热搜榜，右侧会标注排名升降变化。
                </p>
                <Link
                  to="/ranking"
                  className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  前往热搜榜
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </Link>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-lg font-bold text-foreground-950">按频道浏览</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}